import type { Color, Point, Shape, Sprite } from '../content/types';

/**
 * Minimal drawing surface. All coordinates are in *sprite units*; the painter applies
 * the current transform (set with `begin`). Two implementations: Canvas (game) and
 * a software rasteriser (tools/PNG). Anything drawable must go through this so the
 * preview tool and the game agree.
 */
export interface Painter {
  /** Start drawing a sprite: origin (ox,oy) in device px, `scale` px per unit, rotation radians about the sprite centre. */
  begin(ox: number, oy: number, scale: number, rot?: number, cx?: number, cy?: number): void;
  end(): void;
  rect(x: number, y: number, w: number, h: number, fill?: Color, stroke?: Color, lw?: number, rx?: number): void;
  circle(x: number, y: number, r: number, fill?: Color, stroke?: Color, lw?: number): void;
  poly(pts: Point[], fill?: Color, stroke?: Color, lw?: number): void;
  line(pts: Point[], stroke: Color, lw?: number): void;
  /** (Sep 11, the freeze hunt) draw a sprite from a cached bitmap at this scale, unrotated, top-left at device (ox, oy); false = not offered */
  blit?(sprite: Sprite, ox: number, oy: number, scale: number): boolean;
}

export function drawShape(p: Painter, s: Shape): void {
  switch (s.t) {
    case 'rect': p.rect(s.x, s.y, s.w, s.h, s.fill, s.stroke, s.lw, s.rx); break;
    case 'circle': p.circle(s.x, s.y, s.r, s.fill, s.stroke, s.lw); break;
    case 'poly': p.poly(s.pts, s.fill, s.stroke, s.lw); break;
    case 'line': p.line(s.pts, s.stroke, s.lw); break;
  }
}

/** Draw a sprite with its top-left at device (ox,oy), `scale` px per unit, optional rotation about its centre. */
/**
 * (Sep 11) A sprite drawn from a cached bitmap when the painter can (the canvas), else shape by shape: for the things a
 * chunk's ground carries by the hundred — trees, furniture — whose thirty shapes each were most of a ground paint.
 * Only for sprites that are stable objects (content, or cached derivatives): a fresh object every call would miss forever.
 */
export function drawSpriteCached(p: Painter, sprite: Sprite, ox: number, oy: number, scale: number): void {
  if (p.blit?.(sprite, ox, oy, scale)) return;
  drawSprite(p, sprite, ox, oy, scale);
}

export function drawSprite(p: Painter, sprite: Sprite, ox: number, oy: number, scale: number, rot = 0): void {
  p.begin(ox, oy, scale, rot, sprite.size[0] / 2, sprite.size[1] / 2);
  for (const s of sprite.shapes) drawShape(p, s);
  p.end();
}

/** Axis-aligned bounds of all shapes, to lint "shape outside sprite". */
export function spriteBounds(sprite: Sprite): { minX: number; minY: number; maxX: number; maxY: number } {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  const add = (x: number, y: number) => { minX = Math.min(minX, x); minY = Math.min(minY, y); maxX = Math.max(maxX, x); maxY = Math.max(maxY, y); };
  for (const s of sprite.shapes) {
    const lw = (s.lw ?? 0) / 2;
    switch (s.t) {
      case 'rect': add(s.x - lw, s.y - lw); add(s.x + s.w + lw, s.y + s.h + lw); break;
      case 'circle': add(s.x - s.r - lw, s.y - s.r - lw); add(s.x + s.r + lw, s.y + s.r + lw); break;
      case 'poly': case 'line': for (const [x, y] of s.pts) { add(x - lw, y - lw); add(x + lw, y + lw); } break;
    }
  }
  return { minX, minY, maxX, maxY };
}

export function parseColor(c: Color): [number, number, number, number] {
  const n = parseInt(c.slice(1), 16);
  if (c.length === 9) return [(n >>> 24) & 255, (n >>> 16) & 255, (n >>> 8) & 255, n & 255];
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255, 255];
}

/** Darken/lighten a hex colour by factor (0.8 = darker, 1.2 = lighter). */
export function shade(c: Color, f: number): Color {
  const [r, g, b, a] = parseColor(c);
  const cl = (v: number) => Math.max(0, Math.min(255, Math.round(v * f)));
  // (Oct 1, fix 4 of the car redesign) an `#rrggbbaa` keeps its alpha: a see-through shine on a car's shaded side became an opaque stripe
  return '#' + [cl(r), cl(g), cl(b)].map((v) => v.toString(16).padStart(2, '0')).join('') + (a < 255 ? a.toString(16).padStart(2, '0') : '');
}

/** Copy of a sprite with every fill colour multiplied by `f`; strokes untouched unless `strokes` (Oct 1: a vehicle's shaded side — its lines stayed bright) */
export function tintSprite(sprite: Sprite, f: number, strokes = false): Sprite {
  return { size: sprite.size, shapes: sprite.shapes.map((s) => { const o = { ...s } as Shape & { fill?: Color; stroke?: Color }; if ('fill' in s && s.fill) o.fill = shade(s.fill, f); if (strokes && s.stroke) o.stroke = shade(s.stroke, f); return o; }) as Shape[] };
}

/** Copy of a sprite with every fill replaced by one colour and strokes removed (for shadows). */
export function silhouette(sprite: Sprite, color: Color): Sprite {
  return { size: sprite.size, shapes: sprite.shapes.filter((s) => s.t !== 'line').map((s) => ({ ...s, fill: color, stroke: undefined, lw: undefined })) as Shape[] };
}
